/**
 * LinkBookingPanel
 *
 * Contenido de vinculacion manual de bookings sin wrapper <Dialog>.
 * Reutilizable en tabs o cualquier contenedor.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  LinkOutlined,
  SearchOutlined,
  InfoOutlined,
  CheckCircleOutlined,
  HelpOutlineOutlined,
} from "@mui/icons-material";
import { getMatchCandidates, linkBookingEquipment, searchNavieraBookings } from "@/services/reconciliation-api";
import { usePermissions } from "@/contexts/permissions-context";
import { ArchiveBookingDialog } from "../archive-booking";
import type {
  EquipmentLinkSuggestionDto,
  LinkBookingEquipmentRequest,
  MatchCandidateDto,
  NavieraBookingSearchResult,
  RepoDocReconciliationCandidateDto,
} from "@/types/reconciliation";

export interface LinkBookingPanelProps {
  bookingId: number;
  bookingReference: string | null;
  bookingMbl?: string | null;
  bookingOriginPortId?: number | null;
  bookingDestinationPortId?: number | null;
  /**
   * Candidatos ya calculados por reconciliation-health.
   * Si existen, son la fuente primaria del modal para no perder candidatos L4/L5
   * que todavia no necesariamente aparecen en el endpoint historico /candidates.
   */
  initialCandidates?: RepoDocReconciliationCandidateDto[];
  /** Recibe el booking NAVIERA vinculado para que el padre actualice estado */
  onLinked: (navieraBookingId: number, navieraBookingReference: string | null) => void;
}

const LEVEL_META: Record<number, { bg: string; color: string; short: string; label: string; rule: string }> = {
  1: { bg: "#EFF6FF", color: "#1D4ED8", short: "L1", label: "Referencia exacta", rule: "La referencia del booking coincide exactamente" },
  2: { bg: "#F0FDF4", color: "#15803D", short: "L2", label: "MBL + Contenedor", rule: "El MBL y el numero de contenedor coinciden" },
  3: { bg: "#FFF7ED", color: "#C2410C", short: "L3", label: "Ruta + Fechas", rule: "El MBL, los puertos de origen/destino y la ventana de fechas ETD/ETA coinciden" },
  4: { bg: "#FEF3C7", color: "#B45309", short: "L4", label: "Container + revision", rule: "El container coincide; MBL/BL, puertos o fechas pueden venir como senales de advertencia para confirmar manualmente" },
  5: { bg: "#ECFDF5", color: "#047857", short: "L5", label: "Logistica sin MBL/BL", rule: "No hay MBL/BL usable; coincide al menos un container disponible y las senales de entidad, ruta y ETD son compatibles. Puede advertir si NAVIERA trae equipos adicionales." },
};

interface MatchReasonItem {
  text: string;
  value: string | null;
  confirmed: boolean;
}

interface CandidateRow {
  bookingId: number;
  bookingReference: string | null;
  mbl: string | null;
  source: string | null;
  matchLevel?: number | null;
  originPortId?: number | null;
  destinationPortId?: number | null;
  containerId?: string | null;
  etd?: string | null;
  equipmentLinks?: EquipmentLinkSuggestionDto[];
}

function buildMatchReasons(
  level: number,
  candidate: CandidateRow,
  currentMbl: string | null,
  currentOriginPortId: number | null,
  currentDestinationPortId: number | null,
): MatchReasonItem[] {
  switch (level) {
    case 1:
      return [{ text: "Referencia", value: candidate.bookingReference ?? null, confirmed: true }];
    case 2:
      return [
        { text: "MBL", value: candidate.mbl ?? currentMbl ?? null, confirmed: !!(candidate.mbl || currentMbl) },
        { text: "Contenedor", value: null, confirmed: false },
      ];
    case 3:
      return [
        { text: "MBL", value: candidate.mbl ?? currentMbl ?? null, confirmed: !!(candidate.mbl || currentMbl) },
        { text: "Puerto Origen", value: candidate.originPortId != null ? `ID ${candidate.originPortId}` : null, confirmed: candidate.originPortId != null && candidate.originPortId === currentOriginPortId },
        { text: "Puerto Destino", value: candidate.destinationPortId != null ? `ID ${candidate.destinationPortId}` : null, confirmed: candidate.destinationPortId != null && candidate.destinationPortId === currentDestinationPortId },
        { text: "Ventana ETD/ETA", value: null, confirmed: false },
      ];
    case 4:
      return [
        { text: "Container", value: null, confirmed: true },
        { text: "MBL/BL", value: candidate.mbl ?? currentMbl ?? null, confirmed: !!(candidate.mbl && currentMbl && candidate.mbl === currentMbl) },
        { text: "Ruta/fechas", value: null, confirmed: false },
      ];
    case 5:
      return [
        { text: "Container disponible", value: null, confirmed: true },
        { text: "Entidad + ruta", value: null, confirmed: true },
        { text: "ETD compatible", value: null, confirmed: true },
        { text: "MBL/BL", value: "sin identificador usable; revisar advertencias si NAVIERA tiene equipos adicionales", confirmed: false },
      ];
    default:
      return [];
  }
}

function MatchLevelTooltipContent({ level, candidate, currentMbl, currentOriginPortId, currentDestinationPortId }: {
  level: number;
  candidate: CandidateRow;
  currentMbl: string | null;
  currentOriginPortId: number | null;
  currentDestinationPortId: number | null;
}) {
  const meta = LEVEL_META[level];
  const reasons = buildMatchReasons(level, candidate, currentMbl, currentOriginPortId, currentDestinationPortId);

  return (
    <Box sx={{ p: 0.5, minWidth: 220 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>{meta.short} - {meta.label}</Typography>
      <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.7)", mb: 1, fontStyle: "italic" }}>{meta.rule}</Typography>
      <Typography variant="caption" sx={{ display: "block", fontWeight: 600, mb: 0.5 }}>Campos que coinciden:</Typography>
      {reasons.map((r, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.5 }}>
          {r.confirmed
            ? <CheckCircleOutlined sx={{ fontSize: 13, color: "#86efac", mt: "1px", flexShrink: 0 }} />
            : <HelpOutlineOutlined sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)", mt: "1px", flexShrink: 0 }} />}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{r.text}</Typography>
            {r.value && <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", color: "rgba(255,255,255,0.85)" }}>"{r.value}"</Typography>}
            {!r.value && !r.confirmed && <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.45)", fontStyle: "italic" }}>verificado por el sistema</Typography>}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

const formatSource = (s: string | null) => s === "NAVIERA" ? "Naviera" : s === "REPO_DOC" ? "Repo Doc" : (s ?? "-");
const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
};

function fromCandidate(c: MatchCandidateDto): CandidateRow {
  return {
    bookingId: c.bookingId,
    bookingReference: c.bookingReference,
    mbl: c.mbl,
    source: c.source,
    matchLevel: c.matchLevel,
    originPortId: c.originPortId,
    destinationPortId: c.destinationPortId,
    equipmentLinks: c.equipmentLinks,
  };
}

function fromHealthCandidate(c: RepoDocReconciliationCandidateDto): CandidateRow {
  return {
    bookingId: c.bookingId,
    bookingReference: c.bookingReference,
    mbl: null,
    source: "NAVIERA",
    matchLevel: c.matchLevel,
    originPortId: null,
    destinationPortId: null,
    equipmentLinks: c.equipmentLinks,
  };
}

function fromSearchResult(r: NavieraBookingSearchResult): CandidateRow {
  return { bookingId: r.bookingId, bookingReference: r.bookingReference, mbl: r.mbl, source: "NAVIERA", containerId: r.containerId, etd: r.etd, matchLevel: null, equipmentLinks: [] };
}

function equipmentLinkKey(link: EquipmentLinkSuggestionDto): string {
  return `${link.navieraBookingEquipmentId}:${link.repoDocBookingEquipmentId}`;
}

function buildDefaultEquipmentSelection(rows: CandidateRow[]): Record<number, string[]> {
  return rows.reduce<Record<number, string[]>>((selection, row) => {
    if (row.equipmentLinks?.length) {
      selection[row.bookingId] = row.equipmentLinks.map(equipmentLinkKey);
    }

    return selection;
  }, {});
}

export function LinkBookingPanel({
  bookingId,
  bookingReference,
  bookingMbl,
  bookingOriginPortId,
  bookingDestinationPortId,
  initialCandidates,
  onLinked,
}: LinkBookingPanelProps) {
  const { hasPermission } = usePermissions();
  const canWriteBookings = hasPermission("Bookings", "WRITE");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<CandidateRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);
  const [selectedEquipmentLinks, setSelectedEquipmentLinks] = useState<Record<number, string[]>>({});
  const [linkedEquipmentKeys, setLinkedEquipmentKeys] = useState<Set<string>>(() => new Set());

  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CandidateRow[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const isSearchMode = searchResults !== null;
  const initialCandidateRows = useMemo(
    () => initialCandidates?.map(fromHealthCandidate) ?? [],
    [initialCandidates],
  );

  const loadCandidates = useCallback(async (ignoreInitialCandidates = false) => {
    setLoading(true);
    setError(null);
    setCandidates([]);
    setSelectedEquipmentLinks({});
    setLinkedEquipmentKeys(new Set());
    setSearchResults(null);
    setSearchTerm("");
    try {
      if (!ignoreInitialCandidates && initialCandidateRows.length > 0) {
        setCandidates(initialCandidateRows);
        setSelectedEquipmentLinks(buildDefaultEquipmentSelection(initialCandidateRows));
        return;
      }

      const data = await getMatchCandidates(bookingId);
      const rows = data.map(fromCandidate);
      setCandidates(rows);
      setSelectedEquipmentLinks(buildDefaultEquipmentSelection(rows));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar candidatos");
    } finally {
      setLoading(false);
    }
  }, [bookingId, initialCandidateRows]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    setSearchError(null);
    try {
      const results = await searchNavieraBookings(searchTerm);
      setSearchResults(results.map(fromSearchResult));
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : "Error al buscar");
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchResults(null);
    setSearchTerm("");
    setSearchError(null);
  }, []);

  const getSelectedEquipmentLinks = useCallback((candidate: CandidateRow) => {
    if (!candidate.equipmentLinks?.length) return [];

    const selectedKeys = selectedEquipmentLinks[candidate.bookingId] ?? [];
    return candidate.equipmentLinks.filter((link) => {
      const key = equipmentLinkKey(link);
      return selectedKeys.includes(key) && !linkedEquipmentKeys.has(key);
    });
  }, [linkedEquipmentKeys, selectedEquipmentLinks]);

  const toggleEquipmentLink = useCallback((bookingCandidateId: number, link: EquipmentLinkSuggestionDto) => {
    const key = equipmentLinkKey(link);

    setSelectedEquipmentLinks((current) => {
      const currentKeys = current[bookingCandidateId] ?? [];
      const nextKeys = currentKeys.includes(key)
        ? currentKeys.filter((item) => item !== key)
        : [...currentKeys, key];

      return { ...current, [bookingCandidateId]: nextKeys };
    });
  }, []);

  const handleLink = useCallback(async (candidate: CandidateRow) => {
    setLinkingId(candidate.bookingId);
    setLinkError(null);
    setLinkSuccess(null);
    try {
      const selectedLinks = getSelectedEquipmentLinks(candidate);
      const linkedKeys = selectedLinks.map(equipmentLinkKey);
      const equipmentLinks = selectedLinks.map((link) => ({
        navieraBookingEquipmentId: link.navieraBookingEquipmentId,
        repoDocBookingEquipmentId: link.repoDocBookingEquipmentId,
      }));

      if (equipmentLinks.length === 0) {
        setLinkError("Selecciona al menos un par de equipment para vincular. El link manual ya no permite vincular sólo a nivel de booking.");
        return;
      }

      const request: LinkBookingEquipmentRequest = {
        navieraBookingId: candidate.bookingId,
        repoDocBookingId: bookingId,
        equipmentLinks,
      };

      await linkBookingEquipment(request);
      setLinkedEquipmentKeys((current) => {
        const next = new Set(current);
        linkedKeys.forEach((key) => next.add(key));
        return next;
      });
      setSelectedEquipmentLinks((current) => ({
        ...current,
        [candidate.bookingId]: (candidate.equipmentLinks ?? [])
          .map(equipmentLinkKey)
          .filter((key) => !linkedKeys.includes(key)),
      }));
      setLinkSuccess(
        equipmentLinks.length === 1
          ? "Equipo vinculado. Si quedan equipos pendientes, podés seguir vinculándolos sin cerrar esta ventana."
          : "Grupo de equipos vinculado. Si quedan equipos pendientes, podés seguir vinculándolos sin cerrar esta ventana.",
      );
      onLinked(candidate.bookingId, candidate.bookingReference);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : "Error al vincular");
    } finally {
      setLinkingId(null);
    }
  }, [bookingId, getSelectedEquipmentLinks, onLinked]);

  const renderEquipmentCell = (candidate: CandidateRow) => {
    if (!Array.isArray(candidate.equipmentLinks)) {
      return (
        <Typography variant="caption" color="text.disabled">
          Resuelve backend
        </Typography>
      );
    }

    if (candidate.equipmentLinks.length === 0) {
      return (
        <Typography variant="caption" color="warning.main">
          Sin equipment inequivoco
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {candidate.equipmentLinks.map((link) => {
          const key = equipmentLinkKey(link);
          const label = link.containerId ?? `${link.navieraBookingEquipmentId} -> ${link.repoDocBookingEquipmentId}`;
          const linked = linkedEquipmentKeys.has(key);

          return (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  size="small"
                  checked={linked || (selectedEquipmentLinks[candidate.bookingId] ?? []).includes(key)}
                  disabled={linked}
                  onChange={() => toggleEquipmentLink(candidate.bookingId, link)}
                />
              }
              label={
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "monospace", color: linked ? "text.disabled" : "text.primary" }}
                  >
                    {label}
                  </Typography>
                  {linked && <Chip size="small" color="success" variant="outlined" label="Conciliado" />}
                </Box>
              }
              sx={{ m: 0 }}
            />
          );
        })}
      </Box>
    );
  };

  const renderCandidateTable = (rows: CandidateRow[], showLevel: boolean) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Referencia</TableCell>
          <TableCell>MBL</TableCell>
          <TableCell>Fuente</TableCell>
          {showLevel && <TableCell align="center">Nivel</TableCell>}
          {showLevel && <TableCell>Equipment</TableCell>}
          {!showLevel && <TableCell>Contenedor</TableCell>}
          {!showLevel && <TableCell align="right">ETD</TableCell>}
          <TableCell align="center">Accion</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((c) => {
          const meta = c.matchLevel ? LEVEL_META[c.matchLevel] : null;
          const isLinking = linkingId === c.bookingId;
          const selectedLinks = getSelectedEquipmentLinks(c);
          const requiresEquipmentSelection = Array.isArray(c.equipmentLinks);
          const hasNoResolvedEquipment = requiresEquipmentSelection && c.equipmentLinks!.length === 0;
          const allEquipmentLinked = requiresEquipmentSelection && c.equipmentLinks!.length > 0 && c.equipmentLinks!.every((link) => linkedEquipmentKeys.has(equipmentLinkKey(link)));
          const hasNoSelectedEquipment = requiresEquipmentSelection && c.equipmentLinks!.length > 0 && selectedLinks.length === 0;
          const linkDisabled = linkingId !== null || hasNoResolvedEquipment || allEquipmentLinked || hasNoSelectedEquipment;

          return (
            <TableRow key={c.bookingId} hover>
              <TableCell><Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>{c.bookingReference ?? "-"}</Typography></TableCell>
              <TableCell><Typography variant="body2" color="text.secondary">{c.mbl ?? "-"}</Typography></TableCell>
              <TableCell><Typography variant="body2" color="text.secondary">{formatSource(c.source)}</Typography></TableCell>
              {showLevel && (
                <TableCell align="center">
                  {meta && c.matchLevel ? (
                    <Tooltip arrow placement="left" title={<MatchLevelTooltipContent level={c.matchLevel} candidate={c} currentMbl={bookingMbl ?? null} currentOriginPortId={bookingOriginPortId ?? null} currentDestinationPortId={bookingDestinationPortId ?? null} />}>
                      <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.25, cursor: "help" }}>
                        <Chip label={meta.short} size="small" sx={{ fontSize: "0.7rem", height: 22, fontWeight: 700, bgcolor: meta.bg, color: meta.color }} />
                        <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "text.disabled", lineHeight: 1 }}>{meta.label}</Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>
              )}
              {showLevel && <TableCell>{renderEquipmentCell(c)}</TableCell>}
              {!showLevel && <TableCell><Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>{c.containerId ?? "-"}</Typography></TableCell>}
              {!showLevel && <TableCell align="right"><Typography variant="body2">{formatDate(c.etd)}</Typography></TableCell>}
              <TableCell align="center">
                <Button size="small" variant="contained" disableElevation startIcon={isLinking ? <CircularProgress size={12} /> : allEquipmentLinked ? <CheckCircleOutlined /> : <LinkOutlined />} disabled={linkDisabled} onClick={() => handleLink(c)} sx={{ textTransform: "none", minWidth: 90 }}>
                  {isLinking ? "Vinculando..." : allEquipmentLinked ? "Conciliado" : "Vincular"}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={32} /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
  }

  return (
    <Box>
      {canWriteBookings && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            size="small"
            color="error"
            variant="outlined"
            aria-label={`Archivar ${bookingReference ?? `#${bookingId}`}`}
            onClick={() => setArchiveOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Archivar
          </Button>
        </Box>
      )}
      <ArchiveBookingDialog
        open={archiveOpen}
        bookingId={bookingId}
        bookingReference={bookingReference ?? undefined}
        onClose={() => setArchiveOpen(false)}
        onCompleted={() => {
          setArchiveOpen(false);
          void loadCandidates(true);
        }}
      />
      {linkError && <Alert severity="error" sx={{ mb: 2 }}>{linkError}</Alert>}
      {linkSuccess && <Alert severity="success" sx={{ mb: 2 }}>{linkSuccess}</Alert>}

      {!isSearchMode && (
        <>
          {candidates.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No se encontraron candidatos automaticos. Usa el buscador para encontrar el booking naviero manualmente.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Se encontraron <strong>{candidates.length}</strong> candidato(s). Selecciona el booking naviero correcto.
              </Typography>
              {candidates.some((candidate) => candidate.matchLevel != null && candidate.matchLevel >= 4) && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Hay candidatos de confirmacion manual: Core permite vincularlos por container, pero revisa MBL/BL, puertos y fechas antes de confirmar.
                </Alert>
              )}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
                {Object.entries(LEVEL_META).map(([level, meta]) => (
                  <Box key={level} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip label={meta.short} size="small" sx={{ fontSize: "0.65rem", height: 20, fontWeight: 700, bgcolor: meta.bg, color: meta.color }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>{meta.label}</Typography>
                  </Box>
                ))}
                <Tooltip title="Pasa el mouse sobre el chip de nivel para ver que campos coincidieron" arrow>
                  <InfoOutlined sx={{ fontSize: 14, color: "text.disabled", ml: 0.5 }} />
                </Tooltip>
              </Box>
              {renderCandidateTable(candidates, true)}
              <Divider sx={{ my: 2 }} />
            </>
          )}
        </>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {isSearchMode ? "Busqueda libre ? la restriccion de MBL ha sido omitida." : "No encontras el booking? Busqueda libre (sin restriccion de MBL):"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            size="small"
            placeholder="Referencia o MBL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch} disabled={searching || !searchTerm.trim()} startIcon={searching ? <CircularProgress size={12} /> : undefined} sx={{ textTransform: "none" }}>
            {searching ? "Buscando..." : "Buscar"}
          </Button>
          {isSearchMode && <Button variant="text" size="small" onClick={handleClearSearch} sx={{ textTransform: "none" }}>Volver a sugeridos</Button>}
        </Box>
        {searchError && <Alert severity="error" sx={{ mb: 1 }}>{searchError}</Alert>}
        {isSearchMode && (
          <>
            {searchResults!.length === 0
              ? <Alert severity="warning">No se encontraron bookings navieros con ese termino.</Alert>
              : renderCandidateTable(searchResults!, true)}
          </>
        )}
      </Box>
    </Box>
  );
}
